import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from './config';

const LEGACY_PENDING_STATUS = 'pending_verification';
const CURRENT_PENDING_STATUS = 'pendingAdminReview';
const LEGACY_ACTIVE_STATUS = 'approved';
const CURRENT_ACTIVE_STATUS = 'active';

const normalizeListingStatus = (status) => {
  if (status === LEGACY_PENDING_STATUS) return CURRENT_PENDING_STATUS;
  if (status === LEGACY_ACTIVE_STATUS) return CURRENT_ACTIVE_STATUS;
  return status;
};

const normalizeGiftCardDoc = (docSnapshot) => ({
  id: docSnapshot.id,
  ...docSnapshot.data(),
  status: normalizeListingStatus(docSnapshot.data().status),
});

// Create a new gift card listing
export const createGiftCard = async (giftCardData) => {
  try {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
    }

    const createListing = httpsCallable(functions, 'createListing');
    const result = await createListing({
      brand: giftCardData.brand,
      originalValue: giftCardData.cardValue || giftCardData.originalValue,
      sellingPrice: giftCardData.sellingPrice,
      sellerPayout: giftCardData.sellerPayout,
      platformFee: giftCardData.platformFee,
      discount: giftCardData.discount,
      category: giftCardData.category,
      popular: giftCardData.popular || false,
      imageUrl: giftCardData.imageUrl || null,
      imagePath: giftCardData.imagePath || null,
      cardNumber: giftCardData.cardNumber,
      pinCode: giftCardData.pinCode || null,
      sellerEmail: giftCardData.sellerEmail || null,
      sellerName: giftCardData.sellerName || null,
    });

    return result.data.cardId;
  } catch (error) {
    console.error('Error creating gift card:', error);
    throw new Error(error.details || error.message || 'Er is een fout opgetreden bij het aanmaken van de listing.');
  }
};

// Get all available gift cards - 
export const getAvailableGiftCards = async (filters = {}) => {
  try {
    let q = query(
      collection(db, 'giftcards'),
      where('status', 'in', [CURRENT_ACTIVE_STATUS, LEGACY_ACTIVE_STATUS]),
      orderBy('createdAt', 'desc')
    );

    // Apply filters
    if (filters.brand) {
      q = query(q, where('brand', '==', filters.brand));
    }
    if (filters.minValue) {
      q = query(q, where('originalValue', '>=', filters.minValue));
    }
    if (filters.maxValue) {
      q = query(q, where('originalValue', '<=', filters.maxValue));
    }
    if (filters.limit) {
      q = query(q, limit(filters.limit));
    }

    const querySnapshot = await getDocs(q);
    const giftCards = [];
    
    querySnapshot.forEach((doc) => {
      giftCards.push(normalizeGiftCardDoc(doc));
    });
    
    return giftCards;
  } catch (error) {
    throw error;
  }
};

// ✅ Get pending verification gift cards (for admin dashboard)
export const getPendingGiftCards = async () => {
  try {
    const q = query(
      collection(db, 'giftcards'),
      where('status', 'in', [CURRENT_PENDING_STATUS, LEGACY_PENDING_STATUS])
    );
    
    const querySnapshot = await getDocs(q);
    const giftCards = [];
    
    querySnapshot.forEach((doc) => {
      giftCards.push(normalizeGiftCardDoc(doc));
    });
    
    return giftCards;
  } catch (error) {
    throw error;
  }
};

// ✅ Approve a gift card (admin action)
export const approveGiftCard = async (cardId, verifiedBalance) => {
  try {
    const approveListing = httpsCallable(functions, 'approveListing');
    await approveListing({
      cardId,
      verifiedBalance,
    });
    return true;
  } catch (error) {
    throw error;
  }
};

//  Reject a gift card (admin action)
export const rejectGiftCard = async (cardId, reason) => {
  try {
    const rejectListing = httpsCallable(functions, 'rejectListing');
    await rejectListing({
      cardId,
      reason,
    });
    return true;
  } catch (error) {
    throw error;
  }
};

// Get gift cards by seller
export const getGiftCardsBySeller = async (sellerId) => {
  try {
    const q = query(
      collection(db, 'giftcards'),
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const giftCards = [];
    
    querySnapshot.forEach((doc) => {
      giftCards.push(normalizeGiftCardDoc(doc));
    });
    
    return giftCards;
  } catch (error) {
    throw error;
  }
};


// Cancel a gift card listing (only if not sold or reserved)
export const cancelGiftCard = async (cardId, userId) => {
  const docRef = doc(db, 'giftcards', cardId);
  const cardDoc = await getDoc(docRef);
  
  if (!cardDoc.exists()) throw new Error('Cadeaubon niet gevonden');
  
  const card = cardDoc.data();
  if (card.sellerId !== userId) throw new Error('Geen toegang');
  if (card.status === 'sold' || card.status === 'reserved') {
    throw new Error('Deze cadeaubon kan niet meer worden geannuleerd');
  }
  
  await updateDoc(docRef, {
    status: 'cancelled',
    cancelledAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};


// Get single gift card
export const getGiftCard = async (cardId) => {
  try {
    const docRef = doc(db, 'giftcards', cardId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return normalizeGiftCardDoc(docSnap);
    } else {
      throw new Error('Gift card not found');
    }
  } catch (error) {
    throw error;
  }
};

// Update gift card
export const updateGiftCard = async (cardId, updates) => {
  try {
    const docRef = doc(db, 'giftcards', cardId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: new Date()
    });
    return true;
  } catch (error) {
    throw error;
  }
};

// Mark gift card as sold
export const markGiftCardAsSold = async (cardId, buyerId, transactionId) => {
  try {
    const docRef = doc(db, 'giftcards', cardId);
    await updateDoc(docRef, {
      status: 'sold',
      buyerId: buyerId,
      transactionId: transactionId,
      soldAt: new Date()
    });
    return true;
  } catch (error) {
    throw error;
  }
};

// Delete gift card
export const deleteGiftCard = async (cardId) => {
  try {
    await deleteDoc(doc(db, 'giftcards', cardId));
    return true;
  } catch (error) {
    throw error;
  }
};

// Increment gift card views
export const incrementGiftCardViews = async (cardId) => {
  try {
    const docRef = doc(db, 'giftcards', cardId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentViews = docSnap.data().views || 0;
      await updateDoc(docRef, {
        views: currentViews + 1
      });
    }
  } catch (error) {
    // Don't throw error for view tracking
    console.error('Error incrementing views:', error);
  }
};

// Search gift cards -
export const searchGiftCards = async (searchTerm) => {
  try {
    const q = query(
      collection(db, 'giftcards'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const giftCards = [];
    
    querySnapshot.forEach((doc) => {
      const data = {
        ...doc.data(),
        status: normalizeListingStatus(doc.data().status),
      };
      // Simple text matching
      if (data.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
        giftCards.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    return giftCards;
  } catch (error) {
    throw error;
  }
};
